import React, { FC, useEffect, useState } from "react";
import { Expert as IExpert, TutorialItem } from "types/user";
import ClientNav from "components/ClientNav/ClientNav";
import TutorialPage from "screens/server/TutorialPage";
import "i18n";
import { BrowserRouter } from "react-router-dom";
import dynamic from "next/dynamic";
import { ErrorBoundary } from "@sentry/nextjs";
import ErrorView from "components/ErrorView/ErrorView";
import AuthModals from "components/AuthModals/AuthModals";

const AppEntryPoint = dynamic(() => import("screens/browser/App"), {
  ssr: false,
});

export const Tutorial: FC<{
  expert: IExpert;
  tutorial: TutorialItem;
  acceptLanguageHeader: string;
  query;
  isAuthenticated: boolean;
}> = ({ expert, tutorial, acceptLanguageHeader, query, isAuthenticated }) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient && isAuthenticated) {
    return null;
  }

  if (isAuthenticated && isClient) {
    return (
      <ErrorBoundary fallback={() => <ErrorView />}>
        <BrowserRouter>
          <AuthModals />
          <AppEntryPoint />
        </BrowserRouter>
      </ErrorBoundary>
    );
  }
  return (
    <>
      <ClientNav
        hasButtons={false}
        headerColor={true}
        acceptLanguageHeader={acceptLanguageHeader}
      />
      <TutorialPage expert={expert} tutorial={tutorial} query={query} />
    </>
  );
};

export const getServerSideProps = async ({ req, query, resolvedUrl }) => {
  const cookies = req.cookies;

  const user = await fetch(
    process.env.NEXT_PUBLIC_REST_URL + "/users/v1/users/me",
    {
      headers: {
        cookie: `access_token=${cookies?.access_token}; refresh_token=${cookies?.refresh_token}`,
      },
    }
  ).then((res) => res.json());

  const isAuthenticated = Boolean(user?.id);

  const acceptLanguageHeader = req.headers["accept-language"];

  const publicId = query?.publicId;
  const tutorialId = query?.tutorialId;

  const expert = await fetch(
    process.env.NEXT_PUBLIC_REST_URL + "/experts/v1/experts/" + publicId
  ).then((res) => res.json());

  const newPublicId = expert.publicId;

  if (publicId !== newPublicId) {
    return {
      redirect: {
        destination: `/${newPublicId}/product/${tutorialId}`,
        permanent: false,
      },
    };
  }

  const tutorial = await fetch(
    `${process.env.NEXT_PUBLIC_REST_URL}/offers/v1/tutorial-offer/${tutorialId}`
  )
    .then((res) => {
      if (!res.ok) {
        return null;
      }
      return res.json();
    })
    .catch(() => {
      return null;
    });

  if (expert?.id) {
    return {
      props: {
        expert,
        tutorial,
        acceptLanguageHeader,
        query,
        isAuthenticated,
      },
    };
  } else {
    return {
      notFound: true,
    };
  }
};

export default Tutorial;
